import React, { useState, useEffect } from 'react';
import {
    Image,
    PictureAsPdf,
    Grade,
    ChevronRight,
    KeyboardArrowDown,
    CloudDownload,
    Audiotrack,
    Link,
    Lock,
    Visibility
} from '@mui/icons-material';
import { Button, Collapse, Box, LinearProgress, List, Stack, Typography, Tooltip, Grid } from '@mui/material';
import Scrollbars from 'react-custom-scrollbars';
import { ReactComponent as IconVideo } from 'assets/images/icons/c-icono-video.svg';
import { ReactComponent as IconLectura } from 'assets/images/icons/c-icono-ojo-abierto.svg';
import { ReactComponent as IconEvaluacion } from 'assets/images/icons/c-icono-evaluacion.svg';
import { makeStyles, withStyles } from '@mui/styles';
import { styled, useTheme } from '@mui/material/styles';
import moment from 'moment';

const style = {
    boxCursos: {
        boxShadow: 0,
        borderRadius: 0,
        width: '100%',
        mt: 1
    }
};

const BorderLinearProgress = withStyles((theme) => ({
    root: {
        height: 6,
        borderRadius: 0
    },
    colorPrimary: {
        backgroundColor: theme.palette.dark[100]
    },
    bar: {
        borderRadius: 0,
        backgroundColor: theme.palette.success.dark
    }
}))(LinearProgress);

const ContentButton = (props) => {
    const { tipo, data, id, open, calificacion, listaItems } = props;

    const expandIcon = `${tipo}${id}` == open ? <KeyboardArrowDown color="black" /> : <ChevronRight color="black" />;

    const getPorcentaje = (listaItems) => {
        const numItems = listaItems?.length;
        let completos = 0;
        listaItems?.forEach((element) => {
            if (element.isAprobado == 1 && element.calificacion != null) {
                completos = completos + 1;
            }
        });

        const resultado = Math.round((completos / numItems) * 100);
        return isNaN(resultado) ? 0 : resultado;
    };

    const getCalificacion = (listaItems) => {
        const numItems = listaItems?.length;
        let sumCalif = 0;
        listaItems?.forEach((element) => {
            if (element.isAprobado == 1 && element.calificacion != null) {
                sumCalif = sumCalif + Number(element.calificacion);
            }
        });

        const resultado = (sumCalif / numItems).toFixed(2);
        return isNaN(resultado) ? 0 : resultado;
    };

    return (
        <Grid container sx={{ backgroundColor: props.theme.palette.secondary.light }}>
            <Grid item xs={12} sx={{ backgroundColor: props.theme.palette.secondary.light }} display="flex" justifyContent="start">
                {expandIcon}
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="cardTemarioTitle">{data.titulo}</Typography>
                    <Typography variant="caption">{data.descact}</Typography>
                </div>
            </Grid>
            <Grid item xs={12} sx={{ backgroundColor: props.theme.palette.background.default }}>
                <Stack sx={{ width: '100%', p: 1, pb: 0 }}>
                    <props.BorderLinearProgress variant="determinate" value={getPorcentaje(listaItems)} />
                    <Box sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
                        <props.IconTooltip title="Progreso">
                            <Typography sx={{ ml: 0.5 }} variant="cardTemarioFooter">
                                {`${getPorcentaje(listaItems)}%`}
                            </Typography>
                        </props.IconTooltip>
                        <Grade style={{ ...props.theme.typography.iconDefault, margin: '0px 5px 0px 5px' }} color="warning" />
                        <props.IconTooltip title="Promedio">
                            <Typography variant="cardTemarioFooter">{getCalificacion(listaItems)}</Typography>
                        </props.IconTooltip>
                    </Box>
                </Stack>
            </Grid>
        </Grid>
    );
};

const DetalleActividad = (props) => {
    const { detalleActividades, listaDetalles } = props;

    const getDependenciaTitles = (dependencias) => {
        if (!dependencias || dependencias.length === 0) return [];
        return listaDetalles.filter((act) => dependencias.includes(act.idDetalleActividad)).map((act) => act.titulo);
    };

    return (
        <List>
            {listaDetalles?.map((detalleActividad, key) => {
                const dependencias = JSON.parse(detalleActividad.dependencia)?.map((act) => act.idDetalleActividad) || [];
                const isDisabled = props.revisaDependencias(
                    listaDetalles
                        .filter((actividad) => actividad.idCalificacionDetalle != null && actividad.isAprobado == 1)
                        .map((act) => act.idDetalleActividad),
                    dependencias
                );
                const depTitles = getDependenciaTitles(dependencias);

                //  PERIODO DE TIEMPO
                const tienePeriodo = detalleActividad.fechaInicioPeriodo != null && detalleActividad.fechaFinPeriodo != null;
                const periodoActivo = detalleActividad.periodoActivo;
                const periodoFinalizado = detalleActividad.periodoFinalizado;

                // Material bloqueado por periodo (aun no inicia)
                const bloqueadoPorPeriodo = tienePeriodo && !periodoActivo && !periodoFinalizado;

                // Tooltip segun estado
                let tooltipTitle = detalleActividad.titulo;
                if (bloqueadoPorPeriodo) {
                    const fechaInicioFmt = moment(detalleActividad.fechaInicioPeriodo).format('DD/MM/YYYY');
                    const fechaFinFmt = moment(detalleActividad.fechaFinPeriodo).format('DD/MM/YYYY');
                    tooltipTitle = `Disponible desde ${fechaInicioFmt} hasta ${fechaFinFmt}`;
                } else if (tienePeriodo && periodoFinalizado) {
                    tooltipTitle = `Periodo finalizado. Solo puedes visualizar tu avance.`;
                } else if (isDisabled && depTitles.length > 0) {
                    tooltipTitle = `Debes completar: ${depTitles.join(', ')}`;
                }

                const isButtonDisabled =
                    bloqueadoPorPeriodo ||
                    props.revisaDependencias(
                        listaDetalles
                            .filter((actividad) => actividad.idCalificacionDetalle != null && actividad.isAprobado == 1)
                            .map((act) => act.idDetalleActividad),
                        JSON.parse(detalleActividad.dependencia)?.map((act) => act.idDetalleActividad),
                        detalleActividad?.dependenciasCompletas
                    );

                return (
                    <div key={`detalleActividad${detalleActividad.idDetalleActividad}`}>
                        <div>
                            <Tooltip title={tooltipTitle} arrow placement="top">
                                <span style={{ display: 'inline-block', width: '100%' }}>
                                    <Button
                                        sx={{
                                            ...style.boxCursos,
                                            display: 'flex',
                                            justifyContent: 'start',
                                            backgroundColor: props.theme.palette.secondary.light,
                                            // Estilo visual para periodo finalizado
                                            ...(tienePeriodo &&
                                                periodoFinalizado && {
                                                    opacity: 0.7,
                                                    fontStyle: 'italic'
                                                })
                                        }}
                                        onClick={() => {
                                            props.handleDetalleActividad({
                                                ...detalleActividad,
                                                idActividad: props.idActividad,
                                                idTemario: detalleActividad.idTemario,
                                                idSesion: props.idSesion,
                                                idCurso: props.idCurso,
                                                califAprobatoria: props.califAprobatoria,
                                                // Marcar solo lectura si periodo finalizó
                                                soloLectura: tienePeriodo && periodoFinalizado
                                            });
                                        }}
                                        color="inherit"
                                        disabled={isButtonDisabled}
                                    >
                                        {tienePeriodo && periodoFinalizado ? (
                                            <Visibility
                                                sx={{
                                                    ...props.theme.typography.iconDefault,
                                                    marginRight: '5px',
                                                    color: props.theme.palette.grey[500]
                                                }}
                                            />
                                        ) : (
                                            <props.IconTooltip
                                                title={detalleActividad.idCalificacionDetalle ? 'Concluida' : 'Sin responder'}
                                            >
                                                <props.IconType
                                                    type={detalleActividad.idTipoActividad}
                                                    isAprobado={detalleActividad.isAprobado}
                                                    style={{
                                                        ...props.theme.typography.iconDefault,
                                                        marginRight: '5px',
                                                        ...(bloqueadoPorPeriodo && { opacity: 0.5 })
                                                    }}
                                                    {...props}
                                                />
                                            </props.IconTooltip>
                                        )}

                                        <Typography
                                            sx={{
                                                // Color gris si está bloqueado por periodo
                                                ...(bloqueadoPorPeriodo && {
                                                    color: props.theme.palette.grey[500]
                                                })
                                            }}
                                        >
                                            {detalleActividad.titulo}
                                        </Typography>

                                        {bloqueadoPorPeriodo && (
                                            <Lock
                                                sx={{
                                                    fontSize: '0.9rem',
                                                    marginLeft: 'auto',
                                                    color: props.theme.palette.grey[500]
                                                }}
                                            />
                                        )}
                                    </Button>
                                </span>
                            </Tooltip>
                        </div>
                    </div>
                );
            })}
        </List>
    );
};

const Actividad = (props) => {
    const { listaActividades, actividades, detalles } = props;
    const getFullDetalles = (actividad) => {
        const vistos = new Set();
        const resultado = [];
        for (const element of detalles) {
            if (actividad.idActividad == element.idActividad && !vistos.has(element.idDetalleActividad)) {
                vistos.add(element.idDetalleActividad);
                resultado.push(element);
            }
        }
        return resultado;
    };

    const getDependenciaTitles = (dependencias) => {
        if (!dependencias || dependencias.length === 0) return [];
        return listaActividades.filter((act) => dependencias.includes(act.idActividad)).map((act) => act.titulo);
    };

    return (
        <List sx={{ pl: 1, pr: 1 }}>
            {listaActividades?.map((actividad) => {
                const dependencias = JSON.parse(actividad?.dependencia)?.map((act) => act.idActividad) || [];
                const isDisabled = props?.revisaDependencias(
                    listaActividades
                        ?.filter((act) => act?.idCalificacionActividad != null && act?.isAprobado == 1)
                        .map((act) => act?.idActividad),
                    dependencias
                );
                const depTitles = getDependenciaTitles(dependencias);
                const tooltipTitle = isDisabled && depTitles.length > 0 ? `Debes completar: ${depTitles.join(', ')}` : actividad?.titulo;
                return (
                    <div key={`actividades${actividad?.idActividad}`}>
                        <div>
                            <Tooltip title={tooltipTitle}>
                                <span style={{ display: 'inline-block', width: '100%' }}>
                                    <Button
                                        sx={{ ...style.boxCursos, backgroundColor: props.theme.palette.secondary.light }}
                                        onClick={() => {
                                            props.cambiarOpenA(`actividades${actividad?.idActividad}`);
                                        }}
                                        color="inherit"
                                        disabled={props?.revisaDependencias(
                                            listaActividades
                                                ?.filter((act) => act?.idCalificacionActividad != null && act?.isAprobado == 1)
                                                .map((act) => act?.idActividad),
                                            JSON.parse(actividad?.dependencia)?.map((act) => act?.idActividad),
                                            actividad?.dependenciasCompletas
                                        )}
                                    >
                                        <ContentButton
                                            tipo="actividades"
                                            data={actividad}
                                            id={actividad?.idActividad}
                                            calificacion={actividad?.calificacion}
                                            open={props.openA}
                                            listaItems={getFullDetalles(actividad)}
                                            {...props}
                                        />
                                    </Button>
                                </span>
                            </Tooltip>
                            <Collapse in={`actividades${actividad?.idActividad}` == props.openA} timeout="auto" unmountOnExit>
                                <DetalleActividad
                                    listaDetalles={getFullDetalles(actividad)}
                                    idActividad={actividad?.idActividad}
                                    {...props}
                                />
                            </Collapse>
                        </div>
                    </div>
                );
            })}
        </List>
    );
};

const Sesion = (props) => {
    const { sesiones, actividades } = props;
    const getFullActividades = (sesion) => {
        const vistos = new Set();
        const resultado = [];
        for (const element of actividades) {
            if (sesion.idSesion == element.idSesion && !vistos.has(element.idActividad)) {
                vistos.add(element.idActividad);
                resultado.push(element);
            }
        }
        return resultado;
    };

    const getDependenciaTitles = (dependencias) => {
        if (!dependencias || dependencias.length === 0) return [];
        return sesiones.filter((ses) => dependencias.includes(ses.idSesion)).map((ses) => ses.titulo);
    };

    return (
        <List sx={{ backgroundColor: props.theme.palette.background.default, p: 0 }}>
            {sesiones?.map((sesion) => {
                const dependencias = JSON.parse(sesion.dependencia)?.map((ses) => ses.idSesion) || [];
                const isDisabled = props.revisaDependencias(
                    sesiones?.filter((ses) => ses.idCalificacionSesion != null && ses.isAprobado == 1).map((ses) => ses.idSesion),
                    dependencias,
                    sesion.dependenciasCompletas
                );
                const depTitles = getDependenciaTitles(dependencias);
                const tooltipTitle = isDisabled && depTitles.length > 0 ? `Debes completar: ${depTitles.join(', ')}` : sesion.titulo;
                return (
                    <div key={`sesion${sesion.idSesion}`}>
                        <div>
                            <Tooltip title={tooltipTitle}>
                                <span style={{ display: 'inline-block', width: '100%' }}>
                                    <Button
                                        sx={{ ...style.boxCursos, backgroundColor: props.theme.palette.secondary.light, m: 0 }}
                                        onClick={() => {
                                            props.cambiarOpenS(`sesion${sesion.idSesion}`);
                                        }}
                                        color="inherit"
                                        disabled={isDisabled}
                                    >
                                        <ContentButton
                                            tipo="sesion"
                                            data={sesion}
                                            id={sesion.idSesion}
                                            calificacion={sesion.calificacion}
                                            open={props.openS}
                                            listaItems={getFullActividades(sesion)}
                                            {...props}
                                        />
                                    </Button>
                                </span>
                            </Tooltip>
                            <Collapse in={`sesion${sesion.idSesion}` == props.openS} timeout="auto" unmountOnExit>
                                {/* actividades={temario.actividades} idTemario={temario.idTemario} */}
                                {/* {props.curso.tipoCurso == 2 ? ( */}
                                <Actividad
                                    idSesion={sesion.idSesion}
                                    idTemario={sesion.idTemario}
                                    listaActividades={getFullActividades(sesion)}
                                    // idTemario={getTemarioId(sesion)}
                                    {...props}
                                />
                                {/* ) : (
                                    <Temario temarios={sesion.temarios} idSesion={sesion.idSesion} {...props} />
                                )} */}
                            </Collapse>
                        </div>
                    </div>
                );
            })}
        </List>
    );
};

const TemarioV2 = (props) => {
    const { isLoading, ctl, onclick, curso, temario } = props;
    const theme = useTheme();

    const handleDetalleActividad = (id) => {
        onclick(id);
    };

    const revisaDependencias = (arrRespondidas, arrDependencias, dependenciasCompletas) => {
        if (dependenciasCompletas === false) return true;

        if (!arrDependencias || arrDependencias.length === 0) return false;

        const sesiones = props.temario?.sesiones || [];
        const actividades = props.temario?.actividades || [];
        const detalles = props.temario?.detalles || [];

        const depsActivas = arrDependencias.filter((idPadre) => {
            const enDetalles = detalles.find((d) => String(d.idDetalleActividad) === String(idPadre) && String(d.estatus) === '1');
            if (enDetalles) return true;

            const enActividades = actividades.find((a) => String(a.idActividad) === String(idPadre) && String(a.estatus) === '1');
            if (enActividades) return true;

            const enSesiones = sesiones.find((s) => String(s.idSesion) === String(idPadre) && String(s.estatus) === '1');
            if (enSesiones) return true;

            return false;
        });

        if (depsActivas.length === 0) return false;

        const completadas = arrRespondidas.filter((id) => depsActivas.map(String).includes(String(id)));

        return completadas.length < depsActivas.length;
    };

    const IconTooltip = ({ title, children }) => (
        <Tooltip title={title} disableInteractive>
            {children}
        </Tooltip>
    );

    const IconType = (props) => {
        const { type, isAprobado, style } = props;

        const getColor = (aprob) => {
            if (aprob == null || aprob === undefined) {
                return props.theme.palette.dark.main;
            }
            const aprobadoNum = Number(aprob);
            return aprobadoNum === 1 ? props.theme.palette.success.main : props.theme.palette.error.main;
        };

        const colorFinal = getColor(isAprobado);

        const IconWrapper = ({ children }) => (
            <Box
                component="span"
                sx={{
                    display: 'inline-flex',
                    color: colorFinal,
                    '& svg': {
                        fill: `${colorFinal} !important`,
                        color: `${colorFinal} !important`
                    },
                    '& path': {
                        fill: `${colorFinal} !important`
                    },
                    '& *': {
                        fill: `${colorFinal} !important`
                    },
                    ...style
                }}
            >
                {children}
            </Box>
        );

        let icon = null;
        switch (type) {
            case '1':
                icon = (
                    <IconWrapper>
                        <IconLectura />
                    </IconWrapper>
                );
                break;
            case '2':
                icon = (
                    <IconWrapper>
                        <IconVideo />
                    </IconWrapper>
                );
                break;
            case '3':
            case '7':
            case '10':
            case '11':
            case '12':
                icon = (
                    <IconWrapper>
                        <IconEvaluacion />
                    </IconWrapper>
                );
                break;
            case '4':
                icon = <PictureAsPdf sx={{ color: colorFinal, ...style }} />;
                break;
            case '5':
                icon = <Image sx={{ color: colorFinal, ...style }} />;
                break;
            case '6':
                icon = <CloudDownload sx={{ color: colorFinal, ...style }} />;
                break;
            case '8':
                icon = <Audiotrack sx={{ color: colorFinal, ...style }} />;
                break;
            case '9':
                icon = <Link sx={{ color: colorFinal, ...style }} />;
                break;
            default:
                icon = null;
                break;
        }
        return icon;
    };

    const propsTemarios = {
        theme: theme,
        idCurso: curso?.idCurso,
        tipoCurso: curso?.tipoCurso,
        califAprobatoria: curso?.califAprobatoria,
        sesiones: temario?.sesiones,
        actividades: temario?.actividades,
        detalles: temario?.detalles,
        handleDetalleActividad: handleDetalleActividad,
        revisaDependencias: revisaDependencias,
        IconTooltip: IconTooltip,
        IconType: IconType,
        BorderLinearProgress: BorderLinearProgress,
        ContentButton: ContentButton,
        ...props
    };

    return (
        <>
            <Scrollbars style={{ height: '70vh' }}>
                {/* <Curso /> */}
                <Sesion {...propsTemarios} />
            </Scrollbars>
        </>
    );
};

export default TemarioV2;
